import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  ListItem,
  List,
  ListItemText,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Card,
  CardContent,
  Grid,
  Collapse,
  Tooltip,
  TableFooter,
} from "@mui/material";
import {
  Search,
  Close,
  Person,
  Payment,
  Receipt,
  ArrowDownward,
  ArrowUpward,
  AccountBalance,
  CheckCircle,
  Warning,
  ExpandMore,
  ExpandLess,
  ShoppingCart,
} from "@mui/icons-material";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  createCustomerPayment,
  getCustomerTransactions,
  getAllCustomers,
  searchCustomers,
  getAllCustomers2,
} from "../../../api/customers/customers";
import { IonApp, IonButton, IonContent, IonPage, useIonViewWillEnter } from "@ionic/react";
import ReceiptModal from '../../../components/sales/ReceiptModal' ; // Adjust path as needed
import { getBusinessData } from "../../../common/utils";
import ReceiptModal_Large from "../../../components/sales/ReceiptModal_large";
import { MenuButton } from "../../../components/Menu";

interface OrderItem {
  description: any;
  product_id: string;
  quantity: string;
  product_name: string;
  price: string;
  total_amount: string;
  item_status: string;
}

interface Transaction {
  order_id: string;
  order_code: string;
  order_date: string;
  order_status: "COMPLETED" | "PENDING" | "CANCELLED";
  payment_status: '0' | '1' | '2' // 0 = unpaid, 1 = paid, 2 = partial
  total_price: string;
  member_id: string;
  member_name: string;
  mode_of_payment: string;
  order_by: string;
  otp_status: string | null;
  items: OrderItem[];
  balance_due?: number; // calculated field
  payable_amount?: string | null; // calculated field
  // Add other relevant fields as needed
  description?: string;
}

interface Customer {
  id: string;
  user_id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  grade_letter: string;
}

const CustomerAccountPage: React.FC = () => {
  const location = useLocation<{ customer?: Customer }>();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchedCustomers, setSearchedCustomers] = useState<Customer[]>([]);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState({
    customers: false,
    transactions: false,
    payment: false,
  });

  // Payment dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>(
    []
  );
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  useEffect(() => {
    if (location.state && location.state.customer) {
      handleCustomerSelect(location.state.customer);
    }
  }, [location.state]);

  // Calculate totals based on actual data structure
  const unpaidTransactions = transactions.filter(
    (txn) => txn.payment_status == '0' || txn.payment_status == "2"
  );

  const selectedTransactionTotal = unpaidTransactions
    .filter((txn) => selectedTransactions.includes(txn.order_id))
    .reduce((sum, txn) => {
      const amount = txn.total_price || txn.payable_amount || "0";
      const balanceDue = txn.balance_due || amount;
      return sum + Number(balanceDue);
    }, 0); 

  const totalOutstanding = unpaidTransactions.reduce((sum, txn) => {
    const amount = parseFloat(txn.total_price);
    const balanceDue = txn.balance_due || amount;
    return sum + balanceDue;
  }, 0);

  const getPaymentStatusLabel = (status: string | number) => {
    const code = Number(status);
    switch (code) {
      case 0:
        return "Unpaid";
      case 1:
        return "Paid";
      case 2:
        return "Partial";
      default:
        return "Unknown";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "0":
        return "error";
      case "1":
        return "success";
      case "2":
        return "warning";
      default:
        return "default";
    }
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "PENDING":
        return "warning";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const toggleRowExpansion = (orderId: string) => {
    setExpandedRows((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  // Helper function to calculate item total
  const calculateItemTotal = (item: OrderItem) => {
    // First try to use the total_amount field
    const parsedTotal = parseFloat(item.total_amount);
    if (!isNaN(parsedTotal) && parsedTotal > 0) {
      return parsedTotal;
    }
    
    // If total_amount is invalid, calculate from price and quantity
    const price = parseFloat(item.price) || 0;
    const quantity = parseFloat(item.quantity) || 1;
    return price * quantity;
  };

  const fetchCustomerTransactions = async (customerId: string) => {
    try {
      setLoading((prev) => ({ ...prev, transactions: true }));
      const response = await getCustomerTransactions(customerId);
      console.log("API Response:", { response });

      // Process transactions to calculate balance due for partial payments
      const processedTransactions = response.data.orders.map((txn: Transaction) => {
        const totalAmount = parseFloat(txn.payable_amount || txn.total_price);
        let balanceDue = Number(txn.balance_due);

        // If partial payment, calculate remaining balance
        if (txn.payment_status == "2") {
          // You might need to get actual paid amount from another API call
          // For now, assuming 50% paid for partial payments
          balanceDue = totalAmount * 0.5;
        } else if (txn.payment_status == "1") {
          balanceDue = 0;
        }

        // Process items to ensure totals are calculated
        const processedItems = txn.items.map(item => ({
          ...item,
          // Calculate total if not provided
          calculated_total: calculateItemTotal(item)
        }));

        return {
          ...txn,
          balance_due: balanceDue,
          items: processedItems
        };
      });
      
      setTransactions(processedTransactions);
      setBalance(Number(response.data.balance));
      console.log("Processed Transactions:", processedTransactions);

    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading((prev) => ({ ...prev, transactions: false }));
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchQuery("");
    setSearchedCustomers([]);
    setSelectedTransactions([]);
    setPaymentAmount(0);
    setExpandedRows([]);
    fetchCustomerTransactions(customer.user_id);
  };

  const handleTransactionSelect = (transactionId: string) => {
    setSelectedTransactions((prev) => {
      const newSelected = prev.includes(transactionId)
        ? prev.filter((id) => id !== transactionId)
        : [...prev, transactionId];

      // Update payment amount to match selected transactions
      const newTotal = unpaidTransactions
        .filter((txn) => newSelected.includes(txn.order_id))
        .reduce(
          (sum, txn) => sum + (txn.balance_due || parseFloat(txn.total_price)),
          0
        );

      setPaymentAmount(newTotal);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTransactions([]);
      setPaymentAmount(0);
    } else {
      const allUnpaidIds = unpaidTransactions.map((txn) => txn.order_id);
      setSelectedTransactions(allUnpaidIds);
      setPaymentAmount(totalOutstanding);
    }
    setSelectAll(!selectAll);
  };

  const handlePaymentAmountChange = (value: number) => {
    setPaymentAmount(Math.abs(value) || 0);
  };

  const getPaymentType = () => {
    if (paymentAmount === selectedTransactionTotal) return "exact";
    if (paymentAmount > selectedTransactionTotal) return "overpayment";
    if (paymentAmount < selectedTransactionTotal) return "partial";
    return "exact";
  };

  const handlePaymentSubmit = async () => {
    if (!selectedCustomer || selectedTransactions.length === 0 || paymentAmount <= 0) return;

    try {
      setLoading((prev) => ({ ...prev, payment: true }));
      const paymentType = getPaymentType();

      const data = {
        customer_id: selectedCustomer.user_id,
        order_ids: selectedTransactions,
        amount: paymentAmount,
        payment_method: paymentMethod,
        payment_type: paymentType,
        notes: paymentNotes || `Payment for ${selectedTransactions.length} order(s)`,
      };

      const response = await createCustomerPayment(data);
      console.log({response});
      if (response.status !== true) {
        console.error("Payment creation failed:", response.message);
        return;
      }
      // Generate receipt with all payment details
      handleGenerateReceipt(data);
      // Refresh transactions and balance
      await fetchCustomerTransactions(selectedCustomer.user_id);

      // Reset payment dialog
      setShowPaymentDialog(false);
      setSelectedTransactions([]);
      setPaymentAmount(0);
      setPaymentNotes("");
      setSelectAll(false);
    } catch (error) {
      console.error("Error creating payment:", error);
    } finally {
      setLoading((prev) => ({ ...prev, payment: false }));
    }
  };

  const handleSearchChange = async (searchTerm) => {
  
       if (searchTerm.length < 3) {
      setSearchedCustomers([]);
      return;
    }
      try {
        setLoading((prev) => ({ ...prev, customers: true }));
        const response = await searchCustomers(searchTerm);
        setSearchedCustomers(response.data);
      } catch (error) {
        console.error("Error searching customers:", error);
      } finally {
        setLoading((prev) => ({ ...prev, customers: false }));
      }
    };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchedCustomers([]);
      return;
    }
    const timerId = setTimeout(() => {
      handleSearchChange(searchQuery);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchQuery]);

  const fetchAllCustomers = async () => {
    try {
      setLoading((prev) => ({ ...prev, customers: true }));
      const response = await getAllCustomers2();
      setAllCustomers(response.data);
    } catch (error) {
      console.error("Error fetching all customers:", error);
    } finally {
      setLoading((prev) => ({ ...prev, customers: false }));
    }
  };

  useEffect(() => {
    setSelectAll(
      selectedTransactions.length === unpaidTransactions.length &&
        unpaidTransactions.length > 0
    );
  }, [selectedTransactions, unpaidTransactions]);

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);

  const handleGenerateReceipt = (paymentData: any) => {
    if (!selectedCustomer) return;

    const paidTransactions = transactions.filter(txn => 
      paymentData.order_ids.includes(txn.order_id)
    );
    console.log({paidTransactions});
    
    const receiptItems = paidTransactions.flatMap(txn => 
      txn.items.map((item, index) => ({
        orderID: `Item ${index + 1}`,
        productName: item.product_name || 'Unknown Product',
        quantity: item.quantity || '1',
        description: item.description,
        unitPrice: parseFloat(item.price) || 0,
        amount: calculateItemTotal(item), // Use calculated total
        status: item.item_status || 'COMPLETED'
      }))
    );

    const totalAmount = receiptItems.reduce((sum, item) => sum + item.amount, 0);
    const initialBalanceDue = paidTransactions.reduce((sum, txn) => {
      return sum + (txn.balance_due || 0);
    }, 0);

    const paidAmount = paymentData.amount;
    const actualBalanceDue = Math.max(0, initialBalanceDue - paidAmount);

    setReceiptData({
      customer: {
        name: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        phone: selectedCustomer.phone || 'N/A',
        email: selectedCustomer.email || 'N/A'
      },
      order: {
        id: paidTransactions.length === 1 
          ? paidTransactions[0].order_code 
          : `Multiple Orders (${paidTransactions.length})`,
        date: paidTransactions.length === 1 
          ? paidTransactions[0].order_date 
          : new Date().toISOString(),
        status: paidTransactions.length === 1 
          ? paidTransactions[0].order_status 
          : 'COMPLETED',
        paymentStatus: '1',
        paymentMethod: paymentData.payment_method || 'cash'
      },
      items: receiptItems,
      summary: {
        totalAmount,
        paidAmount,
        balanceDue: actualBalanceDue
      },
      payment: {
        amount: paidAmount,
        method: paymentData.payment_method || 'cash',
        date: new Date().toISOString(),
        notes: paymentData.notes || `Payment for ${paidTransactions.length} order(s)`
      }
    });
    setReceiptOpen(true);
  };

  const handlePrintReceipt = (transaction: Transaction) => {
    if (!selectedCustomer) return;

    const receiptItems = transaction.items.map((item, index) => ({
      orderID: `Item ${index + 1}`,
      productName: item.product_name,
      quantity: item.quantity,
      unitPrice: parseFloat(item.price),
      amount: calculateItemTotal(item), // Use calculated total
      description: item.description,
      status: item.item_status
    }));

    const totalAmount = parseFloat(transaction.payable_amount || transaction.total_price);
    const paidAmount = totalAmount - (transaction.balance_due || 0);

    setReceiptData({
      customer: {
        name: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
        phone: selectedCustomer.phone,
        email: selectedCustomer.email
      },
      order: {
        id: transaction.order_code,
        date: transaction.order_date,
        status: transaction.order_status,
        paymentStatus: getPaymentStatusLabel(transaction.payment_status),
        paymentMethod: transaction.mode_of_payment || 'cash'
      },
      items: receiptItems,
      summary: {
        totalAmount,
        paidAmount,
        balanceDue: transaction.balance_due || 0
      }
    });
    setReceiptOpen(true);
  };

  const totalPayableAmount = transactions.reduce(
    (sum, txn) => sum + parseFloat(txn.payable_amount || txn.total_price || '0'),
    0
  );

  const totalBalanceDue = transactions.reduce(
    (sum, txn) => sum + (txn.balance_due || 0),
    0
  );

  // Helper function to calculate total for expanded order items
  const calculateOrderItemsTotal = (items: OrderItem[]) => {
    return items.reduce((sum, item) => {
      return sum + calculateItemTotal(item); // Use the helper function
    }, 0);
  };

  return (
    <IonPage className="pushDown">
      <MenuButton/>
      
      <IonContent>
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Customer Accounts & Payments
          </Typography>

          {/* Customer Search */}
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              placeholder="Search customers by name or phone..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value);
              }
            }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              disabled={!!selectedCustomer}
            />
            {loading.customers && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {searchedCustomers?.length > 0 && (
              <Paper sx={{ mt: 1, maxHeight: '100%', overflow: "auto" }}>
                <List>
                  {searchedCustomers?.map((customer, i) => (
                    <ListItem
                      button
                      key={i}
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      <ListItemText
                        primary={`${customer.firstName} ${customer.lastName}`}
                        secondary={`${customer.phone} 
                         `}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Customer Account Section */}
          {selectedCustomer && (
            <Box>
              {/* Customer Info Header */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ width: 56, height: 56, mr: 2 }}>
                      <Person fontSize="large" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5">
                        {selectedCustomer.firstName} ${selectedCustomer.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCustomer.phone} • {selectedCustomer.email}
                      </Typography>
                    </Box>
                    <IconButton onClick={() => setSelectedCustomer(null)}>
                      <Close />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>

              {/* Balance and Payment Summary */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      bgcolor: balance < 0 ? "error.light" : "success.light",
                    }}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      <AccountBalance sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6">Credit Balance</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                        {formatCurrencyN(balance)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ bgcolor: "warning.light" }}>
                    <CardContent sx={{ textAlign: "center" }}>
                      <Warning sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6">Outstanding</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                        {formatCurrencyN(totalOutstanding)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent sx={{ textAlign: "center" }}>
                      <Receipt sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6">Total Orders</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                        {transactions?.length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Payment Actions */}
              {unpaidTransactions.length > 0 && (
                <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Payment />}
                    onClick={() => setShowPaymentDialog(true)}
                    disabled={selectedTransactions.length === 0}
                  >
                    Record Payment ({selectedTransactions.length} selected)
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSelectedTransactions(
                        unpaidTransactions.map((txn) => txn.order_id)
                      );
                      setPaymentAmount(totalOutstanding);
                      setShowPaymentDialog(true);
                    }}
                  >
                    Pay All Outstanding
                  </Button>
                </Box>
              )}

              {/* Orders Table */}
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Order History
              </Typography>

              {unpaidTransactions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAll}
                        indeterminate={
                          selectedTransactions.length > 0 && !selectAll
                        }
                      />
                    }
                    label={`Select All Unpaid Orders (${formatCurrencyN(
                      totalOutstanding
                    )})`}
                  />
                </Box>
              )}

              {loading.transactions ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : transactions.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">Select</TableCell>
                        <TableCell>Order Code</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Items</TableCell>
                        <TableCell align="right">Total Amount</TableCell>
                        <TableCell align="right">Balance Due</TableCell>
                        <TableCell>Payment Status</TableCell>
                        <TableCell>Order Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((txn) => {
                        const isUnpaid =
                          txn.payment_status == '1' ||
                          txn.payment_status == "2";
                        const isSelected = selectedTransactions.includes(
                          txn.order_id
                        );
                        const isExpanded = expandedRows.includes(txn.order_id);
                        const orderItemsTotal = calculateOrderItemsTotal(txn.items);

                        return (
                          <React.Fragment key={txn.order_id}>
                            <TableRow
                              sx={{
                                bgcolor: isSelected
                                  ? "action.selected"
                                  : "inherit",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <TableCell padding="checkbox">
                                { !isUnpaid
                                 && (
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() =>
                                      handleTransactionSelect(txn.order_id)
                                    }
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  {txn.order_code}
                                </Typography>
                              </TableCell>
                              <TableCell>{txn.order_date}</TableCell>
                              <TableCell>
                                <Chip
                                  icon={<ShoppingCart />}
                                  label={`${txn.items.length} items`}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontWeight: "bold" }}
                              >
                                {formatCurrencyN(
                                  parseFloat(txn.payable_amount)
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{
                                color: (txn.balance_due || 0) > 0 ? 'error.main' : 'success.main',
                                fontWeight: 'bold'
                              }}>
                                {formatCurrencyN(Number(txn.balance_due) || 0)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getPaymentStatusLabel(txn.payment_status)}
                                  size="small"
                                  color={getPaymentStatusColor(txn.payment_status) as any}
                                  icon={Number(txn.payment_status) === 1 ? <CheckCircle fontSize="small" /> : 'not paid'}
                                />

                                {Number(txn.payment_status) === 1 ?  (  
                                  <Tooltip title="Print receipt">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handlePrintReceipt(txn)}
                                      color="primary"
                                    > 
                                      Print
                                    </IconButton>
                                  </Tooltip>
                                ) : ""} 
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={txn.order_status}
                                  size="small"
                                  color={
                                    getOrderStatusColor(txn.order_status) as any
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Tooltip title="View order items">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      toggleRowExpansion(txn.order_id)
                                    }
                                  >
                                    {isExpanded ? (
                                      <ExpandLess />
                                    ) : (
                                      <ExpandMore />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>

                            {/* Expandable row for order items */}
                            <TableRow>
                              <TableCell
                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                colSpan={9}
                              >
                                <Collapse
                                  in={isExpanded}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box sx={{ margin: 1 }}>
                                    <Typography
                                      variant="h6"
                                      gutterBottom
                                      component="div"
                                    >
                                      Order Items
                                    </Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Product</TableCell>
                                          <TableCell align="right">
                                            Quantity
                                          </TableCell>
                                          <TableCell align="right">
                                            Unit Price
                                          </TableCell>
                                          <TableCell align="right">
                                            Total
                                          </TableCell>
                                          <TableCell>Status</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {txn.items.map((item, index) => {
                                          const itemTotal = calculateItemTotal(item);
                                          return (
                                            <TableRow
                                              key={`${txn.order_id}-${index}`}
                                            >
                                              <TableCell>
                                                {item.product_name}
                                                {item.description && (
                                                  <Typography variant="caption" color="text.secondary" display="block">
                                                    {item.description}
                                                  </Typography>
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {item.quantity}
                                              </TableCell>
                                              <TableCell align="right">
                                                {formatCurrencyN(
                                                  parseFloat(item.price)
                                                )}
                                              </TableCell>
                                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                {formatCurrencyN(itemTotal)}
                                              </TableCell>
                                              <TableCell>
                                                <Chip
                                                  label={item.item_status}
                                                  size="small"
                                                  color={
                                                    item.item_status ===
                                                    "COMPLETED"
                                                      ? "success"
                                                      : "default"
                                                  }
                                                />
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                        {/* Total row for order items */}
                                        <TableRow>
                                          <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                                            Order Items Total:
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid #ddd' }}>
                                            {formatCurrencyN(orderItemsTotal)}
                                          </TableCell>
                                          <TableCell></TableCell>
                                        </TableRow>
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                          Grand Totals:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {formatCurrencyN(totalPayableAmount)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {formatCurrencyN(totalBalanceDue)}
                        </TableCell>
                        <TableCell colSpan={3} />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              ) : (
                <Paper sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    No orders found for this customer
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          {/* Payment Dialog */}
          <Dialog
            open={showPaymentDialog}
            onClose={() => setShowPaymentDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Record Payment 
              <IconButton
                sx={{ position: "absolute", right: 8, top: 8 }}
                onClick={() => setShowPaymentDialog(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Customer: {selectedCustomer?.firstName}{" "}
                    {selectedCustomer?.lastName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Selected Orders: {selectedTransactions.length}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Total Amount Due:{" "}
                    <strong>{formatCurrencyN(selectedTransactionTotal)}</strong>
                  </Typography>

                  {/* Selected Orders List */}
                  <Box sx={{ mt: 2, maxHeight: 200, overflow: "auto" }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Orders:
                    </Typography>
                    {unpaidTransactions
                      .filter((txn) =>
                        selectedTransactions.includes(txn.order_id)
                      )
                      .map((txn) => (
                        <Box
                          key={txn.order_id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            py: 0.5,
                          }}
                        >
                          <Typography variant="body2">
                            {txn.order_code}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            {formatCurrencyN(txn.balance_due)}
                          </Typography>
                        </Box>
                      ))}
                  </Box>

                  {/* Payment Type Alert */}
                  {paymentAmount > 0 && (
                    <Alert
                      severity={
                        getPaymentType() === "exact"
                          ? "success"
                          : getPaymentType() === "overpayment"
                          ? "info"
                          : "warning"
                      }
                      sx={{ mt: 2 }}
                    >
                      {getPaymentType() === "exact" &&
                        "Exact payment - all selected orders will be marked as paid"}
                      {getPaymentType() === "overpayment" &&
                        `Overpayment of ${formatCurrencyN(
                          paymentAmount - selectedTransactionTotal
                        )} - excess will be added to account credit`}
                      {getPaymentType() === "partial" &&
                        `Partial payment - ${formatCurrencyN(
                          selectedTransactionTotal - paymentAmount
                        )} will remain outstanding`}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    value={paymentAmount}
                    onChange={(e) =>
                      handlePaymentAmountChange(parseFloat(e.target.value))
                    }
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₦</InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setPaymentAmount(selectedTransactionTotal)}
                    >
                      Exact Amount
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setPaymentAmount(selectedTransactionTotal * 0.5)
                      }
                    >
                      50%
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setPaymentAmount(selectedTransactionTotal * 0.25)
                      }
                    >
                      25%
                    </Button>
                  </Box>

                 <TextField
                    fullWidth
                    select
                    label="Payment Method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="card">POS</MenuItem>
                    <MenuItem value="transfer">Bank Transfer</MenuItem>
                    
                    {/* Conditionally show wallet option based on credit balance AND sufficient balance */}
                    {balance > 0 && paymentAmount <= balance && (
                      <MenuItem value="wallet">Wallet</MenuItem>
                    )}
                   
                    {balance > 0 && paymentAmount > balance && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Wallet balance ({formatCurrencyN(balance)}) is insufficient for this payment ({formatCurrencyN(paymentAmount)})
                      </Alert>
                    )}
                  </TextField>

                  <TextField
                    fullWidth
                    label="Payment Notes"
                    multiline
                    rows={3}
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    placeholder="Optional payment notes..."
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPaymentDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handlePaymentSubmit}
                disabled={
                  !paymentAmount ||
                  selectedTransactions.length === 0 ||
                  loading.payment
                }
                startIcon={
                  loading.payment ? <CircularProgress size={20} /> : <Receipt />
                }
              >
                Record Payment ({formatCurrencyN(paymentAmount)})
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>
      {/* {console.log(getBusinessData().receipt_layout)} */}
      {/* {console.log(getBusinessData())} */}
      {
        getBusinessData() && getBusinessData().receipt_layout === 'large' ? (
          receiptData && (
            <ReceiptModal_Large
              open={receiptOpen}
              onClose={() => setReceiptOpen(false)}
              receiptData={receiptData}
            />
          )
        ) : (
          receiptData && (
            <ReceiptModal
              open={receiptOpen}
              onClose={() => setReceiptOpen(false)}
              receiptData={receiptData}
            />
          )
        )
      }
    </IonPage>
  );
};

export default CustomerAccountPage;